/**
 * The guest's liveness ping (card `t_6d25f68a`, seam 3; D9's route, D12's
 * chrome).
 *
 * `POST /api/demo/session/heartbeat` has existed since D9 and answers 204 for a
 * live guest session — but nothing in the app called it, and the guest TTL is
 * *inactivity*-based (2 h, `services/demo/DemoGuestService`): a visitor who was
 * actively using the demo house was still deleted 2 h after walking in. This is
 * the half that pings — while a guest is in front of this screen one request a
 * minute pushes the deadline forward, and the loop stops for good the moment
 * the hub says this is no longer a live guest session.
 *
 * The inside is deliberately dumb and injectable: this module owns only the
 * timing and the stop rule, and its caller owns both what "a guest is here"
 * means and how the request is made
 * (`composables/useDemoGuestHeartbeat.ts` wires those to the store). Nothing
 * here touches Vue, storage or `fetch`.
 */

/** D9's own wording: "guests answer every 60 s" (`demoGuest.routes.ts`). */
export const GUEST_HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * What one ping says about the loop.
 *   · `ok`    — 204: a live guest session; keep it alive.
 *   · `stop`  — the hub refused it for good (401/403: not a guest session any
 *               more; 404: this hub has no guest door, e.g. an older build).
 *   · `retry` — no verdict (network failure, 5xx): the TTL has not moved, and
 *               the next tick may reach the hub.
 */
export type GuestHeartbeatOutcome = 'ok' | 'stop' | 'retry';

export function classifyGuestHeartbeatAnswer(status: number): GuestHeartbeatOutcome {
  if (status === 204) return 'ok';
  if (status === 401 || status === 403 || status === 404) return 'stop';
  return 'retry';
}

export interface GuestHeartbeatDeps {
  /** Is a guest in front of this screen right now? */
  isGuest: () => boolean;
  /** Send one ping and classify its answer (`classifyGuestHeartbeatAnswer`). */
  ping: () => Promise<GuestHeartbeatOutcome>;
  intervalMs?: number;
  /** Timer functions, injectable so the loop is testable without waiting. */
  setTimeoutImpl?: (fn: () => void, ms: number) => number;
  clearTimeoutImpl?: (handle: number) => void;
  /** Called once, after the loop ended itself ('stop'). For logs. */
  onStop?: () => void;
}

/**
 * Ping while a guest is here. Returns the stopper.
 *
 * The first ping is one full interval out — the session was just created and
 * nothing needs keeping alive yet — and a tick that finds no guest re-arms
 * without calling the hub at all (a session nobody is using is *supposed* to
 * expire; that is what the TTL is for).
 */
export function startGuestHeartbeat(deps: GuestHeartbeatDeps): () => void {
  const intervalMs = deps.intervalMs ?? GUEST_HEARTBEAT_INTERVAL_MS;
  const setTimer = deps.setTimeoutImpl ?? ((fn: () => void, ms: number) => setTimeout(fn, ms) as unknown as number);
  const clearTimer = deps.clearTimeoutImpl ?? ((handle: number) => clearTimeout(handle));
  let handle: number | null = null;
  let stopped = false;

  const tick = async (): Promise<void> => {
    handle = null;
    if (stopped) return;

    if (deps.isGuest()) {
      let outcome: GuestHeartbeatOutcome = 'retry';
      try {
        outcome = await deps.ping();
      } catch {
        outcome = 'retry';
      }
      // The stopper may have run while the ping was in flight.
      if (stopped) return;
      if (outcome === 'stop') {
        stopped = true;
        deps.onStop?.();
        return;
      }
    }

    handle = setTimer(() => void tick(), intervalMs);
  };

  handle = setTimer(() => void tick(), intervalMs);

  return () => {
    stopped = true;
    if (handle !== null) {
      clearTimer(handle);
      handle = null;
    }
  };
}
